import React, { useState, SyntheticEvent, useCallback } from 'react';
import { Alert, Box, Button, TextField, Typography, IconButton, Tooltip, Autocomplete, FilterOptionsState } from '@mui/material';
import DropdownField from '../drop-down/DropdownField';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { CocAnalysisVO, CocSite } from './LabRequests';
import { TabbableAutocomplete } from './../Dropdowns/TabbableAutocomplete';
import AnalysisSelect from '../Dropdowns/AnalysisSelect';

interface SiteOption {
	name: string;
	id: string | number;
	type: 'ATP' | 'COC' | 'NEW';
	inputValue?: string;
	locations: LocationOption[];
}

interface LocationOption {
	name: string;
	id: string | number;
	type: 'ATP' | 'COC' | 'NEW';
	inputValue?: string;
	siteId?: number;
}

interface RootState {
	allSitesLoaded: {
		allSitesData: CocSite[];
	};
}

export interface SampleRow {
	sampleNumber: number;
	sampleName: string;
	sampleSite: string;
	sampleLocation: string;
	sampleType: 'Liquid' | 'Solid' | 'Swab' | '';
	amountCollected: string;
	units: 'mL' | 'g' | 'cm2' | '';
	preserved: boolean;
	collectionDate: any;
	siteOptions: string[];
	locationOptions: string[];
	siteSource: 'ATP' | 'COC' | 'NEW' | '';
	locationSource: 'ATP' | 'COC' | 'NEW' | '';
	siteId: number | string;
	locationId: number | string;
	analyses: number[];
	customAnalysis: string;
	clientSampleId: string;
	foodSampleType: string;
	shelfLife: string;
	storageConditions: string;
	comments: string;
	hasErrors?: {
		[key: string]: boolean;
	};
}

interface SampleTableProps {
	sampleRows: SampleRow[];
	isChampionX: boolean;
	isFoodLab?: boolean;
	sameAnalysesForAll?: boolean;
	cocSites: CocSite[];
	foodAnalyses?: CocAnalysisVO[];
	otherAnalysisId?: number;
	onRowChange: <K extends keyof SampleRow>(index: number, field: K, value: SampleRow[K]) => void;
	onAddRow: () => void;
	onDeleteRow: (index: number) => void;
	setSamplesErrors: (errors: boolean) => void;
	addMoreError: boolean;
	setAddMoreError: (error: boolean) => void;
	setCollectionDateError: (error: boolean) => void;
}

const SampleTable: React.FC<SampleTableProps> = ({ sampleRows, isChampionX=false, isFoodLab=false, sameAnalysesForAll=false, cocSites, foodAnalyses = [], otherAnalysisId, onRowChange, onAddRow, onDeleteRow, setSamplesErrors, addMoreError, setAddMoreError, setCollectionDateError }) => {
	// Get sites from Redux state with proper typing
	const allSitesList = useSelector<RootState, CocSite[]>(state => state.allSitesLoaded.allSitesData || []);
	const [siteInputValues, setSiteInputValues] = useState<Record<number, string>>({});
	const [locationInputValues, setLocationInputValues] = useState<Record<number, string>>({});
	const [newSiteList, setNewSiteList] = useState<SiteOption[]>([]);
	const [newLocationList, setNewLocationList] = useState<LocationOption[]>([]);
	const locationInputRefs = React.useRef<Record<number, React.RefObject<HTMLInputElement>>>({});
	const sampleTypeRefs = React.useRef<Record<number, React.RefObject<HTMLDivElement>>>({});
	// Extract site names and locations with improved typing
	const allSiteNames = React.useMemo(() => {

		let atpList = allSitesList
			.map((site) => ({
				name: site.name,
				id: site.id,
				type: 'ATP' as const,
				locations: site.sampleLocationAllVOs?.map((loc) => ({
					name: loc.name,
					id: loc.id,
					type: 'ATP' as const
				})) || []
			}));

		let cocList = cocSites
			.map((site) => ({
				name: site.name,
				id: site.id,
				type: 'COC' as const,
				locations: site.sampleLocationAllVOs?.map((loc) => ({
					name: loc.name,
					id: loc.id,
					type: 'COC' as const
				})) || []
			}));

		let fullList = [...atpList, ...cocList];

		return fullList.sort((a, b) => a.name.localeCompare(b.name));

	}, [allSitesList]);

	// Get locations for a specific site
	const getLocationsForSite = useCallback((siteName: string): LocationOption[] => {
		const site = allSiteNames.find((s) => s.name === siteName);
		return site?.locations || [];
	}, [allSiteNames]);

	// Filter site options with create functionality
	const filterSiteOptions = useCallback((options: SiteOption[], params: FilterOptionsState<SiteOption>): SiteOption[] => {

		let filtered: SiteOption[] = [];

		if (params.inputValue !== '' && !options.find(option => 
			option.name.toLowerCase() === params.inputValue.toLowerCase()
		)) {
			filtered.push({
				name: params.inputValue,
				id: new Date().getTime(),
				type: 'NEW',
				inputValue: params.inputValue,
				locations: []
			});
		}

		filtered = filtered.concat(options.filter(option =>
			option.name.toLowerCase().includes(params.inputValue.toLowerCase())
		));

		
		return filtered;
	}, []);

	// Filter location options with create functionality
	const filterLocationOptions = useCallback((options: LocationOption[], params: FilterOptionsState<LocationOption>, row: SampleRow): LocationOption[] => {

		let filtered: LocationOption[] = [];
		const updatedRow = sampleRows[sampleRows.findIndex(r => r.sampleNumber === row.sampleNumber)] ?? row;
		if (params.inputValue !== '' && 
			!options.find(option => option.name.toLowerCase() === params.inputValue.toLowerCase())) {
			filtered.push({
				name: params.inputValue,
				id: new Date().getTime(),
				type: 'NEW',
				inputValue: params.inputValue
			});
		}

		filtered = filtered.concat(options.filter(option =>
			option.name.toLowerCase().includes(params.inputValue.toLowerCase()) &&
			(option.siteId !== undefined ? Number(updatedRow.siteId) === option.siteId : true)
		));
		
		return filtered;
	}, [sampleRows]);

	// Validate required fields
	const validateAndUpdateRow = useCallback((idx: number, field: keyof SampleRow, value: any) => {
		onRowChange(idx, field, value);
		const row = sampleRows[idx];
		const errors: { [key: string]: boolean } = { ...(row.hasErrors || {}) };
		if (value) {
			delete errors[field];
			setAddMoreError(false);
		} else if (field !== 'sampleName') {
			errors[field] = true;
			setSamplesErrors(true);
		}

		if (field === 'collectionDate' && value !== null && new Date(value).getTime() > new Date().getTime()) {
			setCollectionDateError(true);
			errors[field] = true;
		} else if (field === 'collectionDate') {
			setCollectionDateError(false);
			errors[field] = false;
		}
		
		onRowChange(idx, 'hasErrors', errors);

		const updatedRows = sampleRows.map((row, i) => {
        if (i !== idx) return row;
			return { ...row, [field]: value };
		});

		const allValid = updatedRows.every(row =>
			isFoodLab ? row.clientSampleId !== "" 
				&& row.collectionDate !== null 
				&& row.collectionDate !== "" 
				&& row.collectionDate !== undefined 
				&& new Date(row.collectionDate).getTime() <= new Date().getTime()
				: row.sampleType !== "" 
				&& row.amountCollected !== "" 
				&& row.collectionDate !== null 
				&& row.collectionDate !== "" 
				&& row.collectionDate !== undefined 
				&& new Date(row.collectionDate).getTime() <= new Date().getTime()
		);

		const nonEmptyNames = updatedRows
			.map(sampleRow => sampleRow.sampleName)
			.filter(sampleName => sampleName !== '');
		const hasDuplicates = nonEmptyNames.length !== new Set(nonEmptyNames).size;

		if (allValid && !hasDuplicates) {
			setSamplesErrors(false);
			setCollectionDateError(false);
		}
	}, [sampleRows, onRowChange, setSamplesErrors, setAddMoreError, setCollectionDateError]);
	const handleAnalysesChange = useCallback((idx: number, newIds: number[]) => {
		onRowChange(idx, 'analyses', newIds);
		const row = sampleRows[idx];
		const errors = { ...(row.hasErrors || {}) };
		if (newIds.length > 0) {
			delete errors.analyses;
			setAddMoreError(false);
		} else {
			errors.analyses = true;
			setSamplesErrors(true);
		}
		if (otherAnalysisId === undefined || !newIds.includes(otherAnalysisId)) {
			delete errors.customAnalysis;
		}
		onRowChange(idx, 'hasErrors', errors);
	}, [sampleRows, onRowChange, setSamplesErrors, setAddMoreError]);

	const handleOtherTextChange = useCallback((idx: number, text: string) => {
		onRowChange(idx, 'customAnalysis', text);
		const row = sampleRows[idx];
		const errors = { ...(row.hasErrors || {}) };
		if (text.trim() !== '' || otherAnalysisId === undefined || !row.analyses.includes(otherAnalysisId)) {
			delete errors.customAnalysis;
		} else {
			errors.customAnalysis = true;
		}
		onRowChange(idx, 'hasErrors', errors);
	}, [sampleRows, onRowChange]);

	const checkDuplicateName = useCallback((idx: number, value: string) => {
		const isDuplicate = sampleRows.some((r, i) => i !== idx && r.sampleName === value && value !== '');
		const errors = { ...(sampleRows[idx].hasErrors || {}) };
		if (isDuplicate) {
			errors.sampleName = true;
			setSamplesErrors(true);
		} else {
			delete errors.sampleName;
		}
		onRowChange(idx, 'hasErrors', errors);

		sampleRows.forEach((otherRow, otherIdx) => {
			if (otherIdx === idx) return;
			const otherErrors = { ...(otherRow.hasErrors || {}) };
			const stillDuplicated = sampleRows.some((comparisonRow, comparisonIdx) =>
				comparisonIdx !== otherIdx && comparisonIdx !== idx && comparisonRow.sampleName === otherRow.sampleName && otherRow.sampleName !== ''
			) || (value === otherRow.sampleName && value !== '');
			if (!stillDuplicated) {
				delete otherErrors.sampleName;
			} else {
				otherErrors.sampleName = true;
			}
			onRowChange(otherIdx, 'hasErrors', otherErrors);
		});
	}, [sampleRows, onRowChange, setSamplesErrors]);





	function updateSiteOptionList (value: any) {
		if ('inputValue' in value) {
			value.type = 'NEW';
			delete value.inputValue;
			setNewSiteList(newSiteList.concat(value));
		}
	}

	function updateLocationOptionList (value: any, siteId: any) {
		if ('inputValue' in value) {
			value.type = 'NEW';
			delete value.inputValue;
			value.siteId = Number(siteId);
			setNewLocationList(newLocationList.concat(value));
		}
	}
	const handleSiteChange = useCallback((idx: number, _: any, newValue: SiteOption | string | null) => {
		validateAndUpdateRow(idx, 'sampleLocation', '');
		validateAndUpdateRow(idx, 'locationSource', '');
		validateAndUpdateRow(idx, 'locationId', '');
		setLocationInputValues(prev => ({ ...prev, [idx]: '' }));
		if (typeof newValue === 'string') {
			const newSiteId = Date.now();
			validateAndUpdateRow(idx, 'sampleSite', newValue);
			validateAndUpdateRow(idx, 'siteSource', 'NEW');
			validateAndUpdateRow(idx, 'siteId', newSiteId);
		} else if (newValue && 'name' in newValue) {
			updateSiteOptionList(newValue);
			validateAndUpdateRow(idx, 'sampleSite', newValue.name);
			validateAndUpdateRow(idx, 'siteSource', newValue.type ?? ('inputValue' in newValue ? 'NEW' : ''));
			validateAndUpdateRow(idx, 'siteId', newValue.id);
		} else {
			validateAndUpdateRow(idx, 'sampleSite', '');
			validateAndUpdateRow(idx, 'siteSource', '');
			validateAndUpdateRow(idx, 'siteId', '');
		}
	}, [validateAndUpdateRow, updateSiteOptionList]);

	const handleLocationChange = useCallback((idx: number, _: any, newValue: LocationOption | string | null, siteId: number | string) => {
		if (typeof newValue === 'string') {
			const newLocationId = Date.now();
			validateAndUpdateRow(idx, 'sampleLocation', newValue);
			validateAndUpdateRow(idx, 'locationSource', 'NEW');
			validateAndUpdateRow(idx, 'locationId', newLocationId);
		} else if (newValue && 'name' in newValue) {
			updateLocationOptionList(newValue, siteId);
			validateAndUpdateRow(idx, 'sampleLocation', newValue.name);
			validateAndUpdateRow(idx, 'locationSource', newValue.type ?? ('inputValue' in newValue ? 'NEW' : ''));
			validateAndUpdateRow(idx, 'locationId', newValue.id);
		} else {
			validateAndUpdateRow(idx, 'sampleLocation', '');
			validateAndUpdateRow(idx, 'locationSource', '');
			validateAndUpdateRow(idx, 'locationId', '');
		}
	}, [validateAndUpdateRow, updateLocationOptionList]);

	return (
		<Box mb={1}>
			<Box sx={{ overflowX: 'auto', mb: 1 }}>
				<table style={{ width: '100%', borderCollapse: 'collapse' }}>
					<thead>
						<tr style={{ background: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
							<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px 5px', backgroundColor:"rgb(229,246,253)" }}><strong>Sample #</strong></th>
							<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px', minWidth: isFoodLab ? 240 : undefined}}>
								{isFoodLab ? "Client Sample ID *" : (isChampionX ? "Sample Point" : "Sample Name")}
							</th>
							{!isFoodLab && (
								<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>{isChampionX ? "Customer Location Code (API14)" : "Sample Site"}</th>
							)}
							{!isFoodLab && !isChampionX && (
								<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>Sample Location</th>
							)}
							{!isFoodLab && (
								<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>Sample Type *</th>
							)}
							{!isFoodLab && (
								<>
									<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px', maxWidth:100 }}>Amount *</th>
									<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px 5px' }}></th>
								</>
							)}
							<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>
								{isFoodLab ? "Date of Sampling *" : "Collection Date *"}
							</th>
							{isFoodLab && (
								<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>Comments</th>
							)}
							{isFoodLab && !sameAnalysesForAll && (
								<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px', minWidth: 200 }}>Analysis Required *</th>
							)}
							<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px 5px'}}></th>
						</tr>
					</thead>
					<tbody>
						{sampleRows.map((row, idx) => {
							if (!locationInputRefs.current[idx])
								locationInputRefs.current[idx] = React.createRef<HTMLInputElement>();
							if (!sampleTypeRefs.current[idx]) 
								sampleTypeRefs.current[idx] = React.createRef<HTMLDivElement>();
							return (
							<tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
								<td style={{ textAlign: 'center', padding: '8px', verticalAlign: 'top', fontSize:"18px", backgroundColor:"rgb(229,246,253)" }}><strong>{row.sampleNumber}</strong></td>

								<td style={{ padding: '8px', verticalAlign: 'top' }}>
									<Box minWidth={120}>
										<TextField
											value={isFoodLab ? row.clientSampleId : row.sampleName}
											onChange={e => validateAndUpdateRow(idx, isFoodLab ? 'clientSampleId' : 'sampleName', e.target.value)}
											onBlur={e => { if (!isFoodLab) checkDuplicateName(idx, e.target.value); }}
											error={isFoodLab ? row.hasErrors?.clientSampleId : undefined}
											size="small"
											variant="outlined"
											fullWidth={isFoodLab}
										/>
										{!isFoodLab && row.hasErrors?.sampleName && row.sampleName !== '' && (
											<Typography variant="caption" color="error" display="block" mt={0.5}>
												Sample names must be different
											</Typography>
										)}
									</Box>
								</td>

								{!isFoodLab && (
									<td style={{ padding: '8px', verticalAlign: 'top' }}>
										<Box minWidth={200}>
											<TabbableAutocomplete<SiteOption>
												value={row.sampleSite ? {
													name: row.sampleSite,
													id: 'existing',
													type: row.siteSource as 'ATP' | 'COC' | 'NEW',
													locations: []
												} : null}
												inputValue={siteInputValues[idx] ?? ''}
												options={[...allSiteNames, ...newSiteList]}
												filterOptions={filterSiteOptions}
												onChange={(_, newValue) => handleSiteChange(idx, null, newValue)}
												onInputChange={(newValue) => setSiteInputValues(prev => ({ ...prev, [idx]: newValue }))}
												nextFocusRef={locationInputRefs.current[idx]}
												placeholder="Select or type to add"
												freeSolo
											/>
										</Box>
									</td>
								)}
								{!isFoodLab && !isChampionX && (
									<td style={{ padding: '8px', verticalAlign: 'top' }}>
										<Tooltip title={!row.sampleSite ? "Select a Sample Site first" : ""}>
											<Box minWidth={200}>
												<TabbableAutocomplete<LocationOption>
													value={row.sampleLocation ? {
														name: row.sampleLocation,
														id: 'existing',
														type: row.locationSource as 'ATP' | 'COC' | 'NEW'
													} : null}
													inputValue={locationInputValues[idx] ?? ''}
													options={[...getLocationsForSite(row.sampleSite), ...newLocationList]}
													filterOptions={(options, params) => filterLocationOptions(options, params, row)}
													onChange={(_, newValue) => handleLocationChange(idx, null, newValue, row.siteId)}
													onInputChange={(newValue) => setLocationInputValues(prev => ({ ...prev, [idx]: newValue }))}
													inputRef={locationInputRefs.current[idx]}
													nextFocusRef={sampleTypeRefs.current[idx]}
													disabled={!row.sampleSite}
													placeholder={row.sampleSite ? "Select or type to add" : "Select a Site first"}
													freeSolo
												/>
											</Box>
										</Tooltip>
									</td>
								)}

								{!isFoodLab && (
									<td style={{ padding: '8px', verticalAlign: 'top' }}>
										<DropdownField
											label=""
											value={row.sampleType}
											onChange={val => validateAndUpdateRow(idx, 'sampleType', val as 'Liquid' | 'Solid' | 'Swab' | '')}
											options={['Liquid', 'Solid', 'Swab']}
											addStyle="!w-[90px]"
											height={40}                  
											selectRef={sampleTypeRefs.current[idx]}
										/>
									</td>
								)}

								{!isFoodLab && (
									<>
									<td style={{ padding: '8px', verticalAlign: 'top' }}>
										<Box width={70}>
											<TextField
												value={row.amountCollected}
												onChange={e => validateAndUpdateRow(idx, 'amountCollected', e.target.value.replace(/[^0-9.]/g, ''))}
												error={row.hasErrors?.amountCollected}
												required
												size="small"
												variant="outlined"
												type="number"
												InputProps={{
													sx: { '& input': { paddingRight: "8px !important" } },
												}}
											/>
										</Box>
									</td>
									<td style={{ paddingTop: '8px', verticalAlign: 'top' }}>
										<Box minWidth={22}>
											<TextField
												value={row.units}
												disabled
												size="small"
												variant="outlined"
												InputProps={{
													disableUnderline: true,
													sx: {
														border: 'none',
														backgroundColor: 'transparent',
														padding: 0,
														fontSize: '16px',
														color: 'inherit',
														'& input': {
															paddingLeft: "0 !important",
															paddingRight: "4px !important",
														},
													},
												}}
												sx={{
													'& .MuiOutlinedInput-notchedOutline': { border: 'none' },
													'& .Mui-disabled': {
														color: 'rgba(0, 0, 0, 0.87)',
														WebkitTextFillColor: 'inherit',
													},
													width: '33px',
												}}
											/>
										</Box>
									</td>
									</>
								)}

								<td style={{ padding: '8px', verticalAlign: 'top' }}>
									<Box minWidth={120}>
										<TextField
											type="date"
											value={row.collectionDate ? new Date(row.collectionDate).toISOString().split('T')[0] : ''}
											onChange={e => validateAndUpdateRow(idx, 'collectionDate', e.target.value)}
											error={row.hasErrors?.collectionDate}
											required
											size="small"
											variant="outlined"
											InputLabelProps={{ shrink: true }}
											fullWidth={isFoodLab}
										/>
									</Box>
								</td>

								{isFoodLab && (
									<td style={{ padding: '8px', verticalAlign: 'top' }}>
										<Box minWidth={120}>
											<TextField
												value={row.comments}
												onChange={e => onRowChange(idx, 'comments', e.target.value)}
												size="small"
												inputProps={{ maxLength: 255 }}
												variant="outlined"
												fullWidth={isFoodLab}
											/>
										</Box>
									</td>
								)}

								{isFoodLab && !sameAnalysesForAll && (
									<td style={{ padding: '8px', verticalAlign: 'top' }}>
										<AnalysisSelect
											analyses={foodAnalyses}
											selectedIds={row.analyses}
											otherText={row.customAnalysis}
											onChange={(ids) => handleAnalysesChange(idx, ids)}
											onOtherTextChange={(text) => handleOtherTextChange(idx, text)}
											error={row.hasErrors?.analyses}
											errorMessage="Select at least one analysis."
											otherError={row.hasErrors?.customAnalysis}
											width={200}
										/>
									</td>
								)}

								<td style={{ padding: '12px 2px', verticalAlign: 'top' }}>
									<IconButton
										aria-label="delete"
										onClick={() => onDeleteRow(idx)}
										size="small"
										disabled={sampleRows.length === 1}
										color='error'
									>
										<DeleteIcon fontSize="small" color='error'/>
									</IconButton>
								</td>
							</tr>
							);
						})}
					</tbody>
				</table>
			</Box>
			{addMoreError && (
				<Typography variant="body2" color="red" paragraph>
					{isFoodLab
						? "Maximum of 20 samples per submission. To submit more, please complete an additional form."
						: "There is a maximum of 10 samples per request. To add more, submit this form again."}
				</Typography>
			)}
			<Button variant="outlined" onClick={onAddRow} sx={{ mt: 1 }} disabled={addMoreError}>
				+ Add another
			</Button>
			

			<Alert
				sx={{
					border: 1,
					padding: "6px 14px",
					mt: 2,
					fontFamily: 'jost',
					fontSize: '16px',
					maxWidth: '430px',
					color: '#2C2C2C',
					}}
				severity="info"
			>
				<Box display="flex" alignItems="center" fontFamily="jost">
				<Typography component="div" sx={{ maxWidth: 260 }} mr={3}>
					<strong>Important:</strong> Clearly write the Sample # (e.g., 1, 2, 3{isFoodLab ? '…' : '...'}) on each individual sample container.
				</Typography>
				<img
					style={{ opacity: 0.8, maxHeight: "70px" }}
					src="/assets/icons/samplenumber.png"
					alt="Sample Number Example"
				/>
				</Box>

				</Alert>
		</Box>
	);
};

export default SampleTable;